import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useClinic } from '@/contexts/ClinicContext'
import { billingService, BillingStatus } from '@/services/billingService'
import { AlertTriangle, X, CreditCard, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'

export const BillingSetupBanner: React.FC = () => {
  const { clinic } = useClinic()
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(false)

  const { data: status } = useQuery<BillingStatus | null>({
    queryKey: ['billingStatus', clinic?.id],
    queryFn: () => (clinic?.id ? billingService.getBillingStatus(clinic.id) : null),
    enabled: !!clinic?.id,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  })

  if (!status) return null

  const handleManageBilling = () => {
    navigate('/settings?tab=billing')
  }

  // ── Past due: payment failed ──────────────────────────────────────────────
  if (status.billingStatus === 'past_due') {
    return (
      <div className="mb-3 rounded-xl border border-red-200 bg-white px-5 py-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-sm font-medium text-red-800">
              Payment failed — please update your payment method to avoid service interruption.
            </p>
          </div>
          <Button size="sm" variant="destructive" onClick={handleManageBilling} className="rounded-full">
            Update Payment
          </Button>
        </div>
      </div>
    )
  }

  // ── Active but scheduled for cancellation ─────────────────────────────────
  if (status.billingStatus === 'active' && status.cancelAt) {
    if (dismissed) return null
    const cancelDate = new Date(status.cancelAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    return (
      <div className="mb-3 rounded-xl border border-brand-warm-gray bg-white px-5 py-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-warm-gray/50">
              <Clock className="h-4 w-4 text-brand-terracotta" />
            </div>
            <p className="text-sm font-medium text-foreground">
              Subscription cancels on {cancelDate} — renew now to keep automatic invoicing active.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleManageBilling}
              className="rounded-full bg-brand-teal text-white hover:bg-brand-teal/90"
            >
              Manage Billing
            </Button>
            <button
              onClick={() => setDismissed(true)}
              className="rounded-full p-1.5 text-muted-foreground hover:bg-brand-warm-gray/50 hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Subscription was cancelled (previously set up) ────────────────────────
  if (status.billingStatus === 'cancelled') {
    if (dismissed) return null
    return (
      <div className="mb-3 rounded-xl border border-brand-terracotta/20 bg-white px-5 py-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-terracotta/10">
              <AlertTriangle className="h-4 w-4 text-brand-terracotta" />
            </div>
            <p className="text-sm text-foreground">
              <span className="font-semibold">Subscription cancelled</span> — reactivate billing to continue invoicing.
              {status.unbilledCount > 0 && (
                <span className="ml-1 text-muted-foreground">
                  ({status.unbilledCount} unbilled event{status.unbilledCount !== 1 ? 's' : ''},{' '}
                  ${status.unbilledTotal.toFixed(2)} pending)
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleManageBilling}
              className="rounded-full bg-brand-teal text-white hover:bg-brand-teal/90"
            >
              Reactivate Billing
            </Button>
            <button
              onClick={() => setDismissed(true)}
              className="rounded-full p-1.5 text-muted-foreground hover:bg-brand-warm-gray/50 hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Never set up (active or trialing hides banner) ────────────────────────
  if (status.billingStatus === 'active' || status.billingStatus === 'trialing') {
    return null
  }

  // ── pending_setup: billing never configured ───────────────────────────────
  const daysSinceSkip = status.setupSkippedAt
    ? Math.floor((Date.now() - new Date(status.setupSkippedAt).getTime()) / (1000 * 60 * 60 * 24))
    : 0

  const isUrgent = daysSinceSkip >= 7

  if (dismissed && !isUrgent) return null

  return (
    <div className={`mb-2 rounded-xl border px-5 py-3.5 ${isUrgent ? 'border-brand-terracotta/20 bg-white' : 'border-brand-warm-gray bg-white'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${isUrgent ? 'bg-brand-terracotta/10' : 'bg-brand-warm-gray/50'}`}>
            <CreditCard className={`h-4 w-4 ${isUrgent ? 'text-brand-terracotta' : 'text-brand-teal'}`} />
          </div>
          <p className={`text-sm ${isUrgent ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
            {isUrgent
              ? "Billing setup overdue — services are being tracked but won't be invoiced until you add a payment method."
              : 'Billing setup incomplete — add a payment method to enable automatic invoicing.'}
            {status.unbilledCount > 0 && (
              <span className="ml-1 text-muted-foreground">
                ({status.unbilledCount} unbilled event{status.unbilledCount !== 1 ? 's' : ''}, ${status.unbilledTotal.toFixed(2)})
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleManageBilling}
            className="rounded-full bg-brand-teal text-white hover:bg-brand-teal/90"
          >
            Set Up Billing
          </Button>
          {!isUrgent && (
            <button
              onClick={() => setDismissed(true)}
              className="rounded-full p-1.5 text-muted-foreground hover:bg-brand-warm-gray/50 hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default BillingSetupBanner
